import React, { useRef, useState } from "react";
import Modal from "../../../../components/Modal";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import bulkSearchTemplate from "../../../../assets/documents/Bulk search template.xlsx";
import "./index.scss";

// ── Download the bundled xlsx template ───────────────────────────────────────
const downloadTemplate = () => {
  const a = document.createElement("a");
  a.href = bulkSearchTemplate;
  a.download = "Bulk search template.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
};

// ── Excel file icon (primary blue theme) ─────────────────────────────────────
const ExcelIcon = ({ size = 44 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 44 44"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Page body */}
    <rect
      x="6"
      y="2"
      width="24"
      height="32"
      rx="3"
      fill="#eff6ff"
      stroke="#2563EB"
      strokeWidth="1.5"
    />
    {/* Folded top-right corner */}
    <path
      d="M24 2v8h6"
      stroke="#2563EB"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M24 2l6 8H24z" fill="#2563EB" opacity="0.25" />
    {/* Row lines */}
    <line
      x1="10"
      y1="17"
      x2="26"
      y2="17"
      stroke="#2563EB"
      strokeWidth="1.2"
      strokeLinecap="round"
      opacity="0.5"
    />
    <line
      x1="10"
      y1="21"
      x2="24"
      y2="21"
      stroke="#2563EB"
      strokeWidth="1.2"
      strokeLinecap="round"
      opacity="0.4"
    />
    <line
      x1="10"
      y1="25"
      x2="22"
      y2="25"
      stroke="#2563EB"
      strokeWidth="1.2"
      strokeLinecap="round"
      opacity="0.3"
    />
    {/* XLS badge at bottom */}
    <rect x="6" y="28" width="14" height="7" rx="2" fill="#2563EB" />
    <text
      x="8.5"
      y="34"
      fontSize="5.5"
      fontWeight="700"
      fill="#fff"
      fontFamily="Arial,sans-serif"
    >
      XLS
    </text>
    {/* Upload arrow badge (bottom-right) */}
    <circle cx="34" cy="34" r="9" fill="#2563EB" />
    <path
      d="M34 38v-8M30 34l4-4 4 4"
      stroke="#fff"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ── Small Excel icon for selected state ───────────────────────────────────────
const ExcelIconSmall = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="4"
      y="2"
      width="18"
      height="26"
      rx="2.5"
      fill="#eff6ff"
      stroke="#2563EB"
      strokeWidth="1.4"
    />
    <path
      d="M17 2v6h5"
      stroke="#2563EB"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M17 2l5 6H17z" fill="#2563EB" opacity="0.2" />
    <line
      x1="7"
      y1="13"
      x2="19"
      y2="13"
      stroke="#2563EB"
      strokeWidth="1.1"
      strokeLinecap="round"
      opacity="0.5"
    />
    <line
      x1="7"
      y1="17"
      x2="17"
      y2="17"
      stroke="#2563EB"
      strokeWidth="1.1"
      strokeLinecap="round"
      opacity="0.4"
    />
    <line
      x1="7"
      y1="21"
      x2="15"
      y2="21"
      stroke="#2563EB"
      strokeWidth="1.1"
      strokeLinecap="round"
      opacity="0.3"
    />
    <rect x="4" y="22" width="10" height="6" rx="1.5" fill="#2563EB" />
    <text
      x="6"
      y="27"
      fontSize="4.5"
      fontWeight="700"
      fill="#fff"
      fontFamily="Arial,sans-serif"
    >
      XLS
    </text>
  </svg>
);

// ── Main component ────────────────────────────────────────────────────────────
const BulkSearchModal = ({ isOpen, onClose, onSearch, loading }) => {
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const isValidFile = (f) =>
    /\.(xlsx|xls|csv)$/i.test(f.name) ||
    [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
      "text/csv",
    ].includes(f.type);

  const handleFileSelect = (f) => {
    if (!f) return;
    if (!isValidFile(f)) {
      alert("Please upload .xlsx, .xls or .csv");
      return;
    }
    setFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFileSelect(e.dataTransfer.files?.[0]);
  };

  const handleSearch = async () => {
    if (!file || loading) return;
    await onSearch(file);
    setFile(null);
  };

  const handleClose = () => {
    setFile(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Bulk Search Entities"
      size="small"
    >
      <div className="bsm">
        {/* Info banner */}
        {/* <div className="bsm__info-banner">
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ flexShrink: 0 }}
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>
            Upload an Excel file with Entity IDs or Names to search multiple
            entities at once.
          </span>
        </div> */}

        {/* Step guide */}
        {/* <div className="bsm__steps">
          {[
            { n: "1", text: "Download template" },
            { n: "2", text: "Fill Entity IDs" },
            { n: "3", text: "Upload & search" },
          ].map((s, i, arr) => (
            <React.Fragment key={s.n}>
              <div className="bsm__step">
                <span className="bsm__step-num">{s.n}</span>
                <span className="bsm__step-text">{s.text}</span>
              </div>
              {i < arr.length - 1 && (
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#9ca3af"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              )}
            </React.Fragment>
          ))}
        </div> */}

        {/* Download template */}
        <button className="bsm__template-btn" onClick={downloadTemplate}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Download Bulk Search Template
          <span className="bsm__template-badge">.xlsx</span>
        </button>

        {/* Drop zone */}
        <div
          className={`bsm__drop-zone${dragging ? " bsm__drop-zone--drag" : ""}${file ? " bsm__drop-zone--has-file" : ""}`}
          onClick={() => !loading && inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: "none" }}
            onChange={(e) => {
              handleFileSelect(e.target.files?.[0]);
              e.target.value = "";
            }}
          />

          {file ? (
            <div className="bsm__file-selected">
              <ExcelIconSmall />
              <div className="bsm__file-info">
                <p className="bsm__file-name">{file.name}</p>
                <p className="bsm__file-size">
                  {(file.size / 1024).toFixed(1)} KB · Ready to search
                </p>
              </div>
              <button
                className="bsm__file-remove"
                onClick={(e) => {
                  e.stopPropagation();
                  setFile(null);
                }}
                title="Remove file"
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          ) : (
            <>
              <ExcelIcon size={48} />
              <p className="bsm__drop-title">Choose File or Drag &amp; Drop</p>
              <p className="bsm__drop-hint">Excel or CSV — .xls, .xlsx, .csv</p>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="bsm__footer">
          <button
            className="bsm__btn bsm__btn--cancel"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="bsm__btn bsm__btn--search"
            onClick={handleSearch}
            disabled={!file || loading}
          >
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: "7px" }}>
                <CircularProgress
                  size={14}
                  thickness={5}
                  sx={{ color: "#fff" }}
                />
                <span>Searching…</span>
              </Box>
            ) : (
              <>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                Search Entities
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default BulkSearchModal;
